import { ReactNode } from 'react';
import React from 'react';
import LogoutDialogContainer from '@apps/containers/LogoutDialogContainer';
import { Theme, useMediaQuery } from '@mui/material';
import { Logo } from '@stack/components';
import { MainLayout } from '@webbyx/mui';
import { useAuthIdentity } from '@webbyx/next-js';

import { useNavigationMenuItems } from './menu.config';
import { useUserDropdownMenuItems } from './user-dropdown.config';

export type LayoutProps = {
  children: ReactNode;
  contentHeightFixed?: boolean;
};

export const Layout: React.FC<LayoutProps> = (props) => {
  const { contentHeightFixed, children } = props;

  // ======================= STATE
  const [dialogOpen, setDialogOpen] = React.useState(false);

  // ======================= HOOKS
  /**
   *  The below variable will hide the current layout menu at given screen size.
   *  The menu will be accessible from the Hamburger icon only (Vertical Overlay Menu).
   *  You can change the screen size from which you want to hide the current layout menu.
   *  Please refer useMediaQuery() hook: https://mui.com/material-ui/react-use-media-query/,
   *  to know more about what values can be passed to this hook.
   *  ! Do not change this value unless you know what you are doing. It can break the template.
   */
  const hidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const { authUser } = useAuthIdentity();
  const menuItems = useNavigationMenuItems();
  const userItems = useUserDropdownMenuItems({
    onLogout: () => {
      setDialogOpen(true);
    },
    authUser,
  });

  // ======================= VIEWS
  return (
    <MainLayout
      name={<Logo alt="Logo" />}
      company="WeSettle Sdn Bhd"
      branding={<Logo type="small" alt="Logo" />}
      brandingLink="/dashboard"
      hidden={hidden}
      enableFixedContentHeight={contentHeightFixed}
      verticalLayoutProps={{
        navigation: {
          items: menuItems,
        },
        appBar: {
          rightItems: [
            { type: 'mode' },
            // { type: 'language' },
            {
              type: 'user',
              props: {
                url: '/settings',
                name: authUser?.user_fullname,
                role: authUser?.user_role?.name,
                avatar: authUser?.user_profile_image?.thumb,
              },
              items: userItems,
            },
          ],
        },
      }}
    >
      {children}

      {/*  =============== LOGOUT */}
      <LogoutDialogContainer
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </MainLayout>
  );
};

// ======================= EXPORT
export default Layout;
